import React, { useState, useEffect } from "react";
import { Search, Check, Loader2, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Alert, AlertDescription } from "@/components/ui/alert";
import SettingsContentWrapper from "@/components/settings/SettingsContentWrapper";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { integrationConfigService } from "@/services/integrationConfigService";
import {
  SiSlack,
  SiGmail,
  SiGithub,
  SiLinear,
  SiTrello,
  SiHubspot,
} from "react-icons/si";

// Available integrations via Composio
const availableIntegrations = [
  {
    id: "gmail",
    name: "Gmail",
    description: "Send and manage emails",
    icon: <SiGmail className="h-6 w-6 text-[#EA4335]" />,
    category: "Communication",
  },
  {
    id: "slack",
    name: "Slack",
    description: "Team communication",
    icon: <SiSlack className="h-6 w-6 text-[#4A154B]" />,
    category: "Communication",
  },
  {
    id: "github",
    name: "GitHub",
    description: "Code management",
    icon: <SiGithub className="h-6 w-6 text-[#181717]" />,
    category: "DevOps",
  },
  {
    id: "linear",
    name: "Linear",
    description: "Issue tracking",
    icon: <SiLinear className="h-6 w-6 text-[#5E6AD2]" />,
    category: "Project Management",
  },
  {
    id: "trello",
    name: "Trello",
    description: "Project boards",
    icon: <SiTrello className="h-6 w-6 text-[#0079BF]" />,
    category: "Project Management",
  },
  {
    id: "hubspot",
    name: "HubSpot",
    description: "CRM & Marketing",
    icon: <SiHubspot className="h-6 w-6 text-[#FF7A59]" />,
    category: "CRM",
  },
];

const IntegrationsPageContent = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [connections, setConnections] = useState([]);
  const [connecting, setConnecting] = useState("");
  const [configuredApps, setConfiguredApps] = useState<string[]>([]);
  const [authConfigs, setAuthConfigs] = useState<Record<string, string>>({});
  const [loadingTools, setLoadingTools] = useState(false);
  const [availableTools, setAvailableTools] = useState<any[]>([]);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const [savingTools, setSavingTools] = useState(false);
  const { user, organization } = useAuth();
  const { toast } = useToast();

  // Load connections and check which apps are configured
  useEffect(() => {
    if (organization?.id) {
      loadConnections();
      loadIntegrationConfigs();

      // Check for OAuth callback success
      const urlParams = new URLSearchParams(window.location.search);
      const status = urlParams.get("status");
      const tab = urlParams.get("tab");

      if (status === "success" && tab === "integrations") {
        // Find the newly connected integration and open drawer
        setTimeout(() => {
          const latestConnection = connections[connections.length - 1];
          if (latestConnection) {
            const integration = availableIntegrations.find(
              (i) => i.id === (latestConnection as any).tool,
            );
            if (integration) {
              handleSelectIntegration(integration);
              toast({
                title: "Connection Successful!",
                description:
                  "Now select the tools you want to use with this integration.",
              });
            }
          }
        }, 1000);

        // Clean up URL
        window.history.replaceState({}, "", "/settings?tab=integrations");
      }
    }
  }, [organization?.id]);

  const loadConnections = async () => {
    try {
      if (!organization?.id) return;

      const data = await integrationConfigService.getOrganizationConnections(
        organization.id,
      );
      setConnections(data || []);
    } catch (error) {
      console.error("Error loading connections:", error);
    }
  };

  const loadIntegrationConfigs = async () => {
    try {
      const { configuredApps, authConfigs } =
        await integrationConfigService.getIntegrationConfigs();
      setConfiguredApps(configuredApps);
      setAuthConfigs(authConfigs);
      console.log("📋 UI loaded integration configs:", {
        configuredApps,
        authConfigs,
      });
    } catch (error) {
      console.error("Error loading integration configs:", error);
    }
  };

  const isConnected = (appId: string) => {
    return connections.some((c: any) => c.tool === appId && c.is_active);
  };

  // Only show configured integrations
  const configuredIntegrations = availableIntegrations.filter((integration) =>
    configuredApps.includes(integration.id),
  );

  const filteredIntegrations = configuredIntegrations.filter((integration) =>
    integration.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleSelectIntegration = async (integration: any) => {
    setSelectedIntegration(integration);
    // Don't clear tools here - only clear when new ones load
    setIsDrawerOpen(true);

    // If connected, automatically load tools
    if (isConnected(integration.id)) {
      await loadToolsForIntegration(integration);
    }
  };

  const loadToolsForIntegration = async (integration: any) => {
    if (!organization?.id) return;

    try {
      setLoadingTools(true);
      setAvailableTools([]); // Clear tools when loading starts
      // Don't clear selectedTools - keep user's previous selections
      const tools = await integrationConfigService.getConnectionTools(
        organization.id,
        integration.id,
      );
      setAvailableTools(tools);

      // Load currently selected tools from organization preferences
      // This would need a new table or use composio_tools with org preferences
      // For now, we'll select all by default on first load
      if (tools.length > 0 && selectedTools.length === 0) {
        // Select essential tools by default
        const essentialTools = tools
          .filter(
            (t: any) =>
              t.name?.includes("SEND") ||
              t.name?.includes("FETCH") ||
              t.name?.includes("GET"),
          )
          .map((t: any) => t.name);
        setSelectedTools(essentialTools);
      }
    } catch (error) {
      console.error("Error loading tools:", error);
    } finally {
      setLoadingTools(false);
    }
  };

  const handleToolToggle = (toolName: string) => {
    setSelectedTools((prev) =>
      prev.includes(toolName)
        ? prev.filter((t) => t !== toolName)
        : [...prev, toolName],
    );
  };

  const handleSaveTools = async () => {
    if (!organization?.id || !selectedIntegration) return;

    try {
      setSavingTools(true);

      // Sync selected tools to composio_tools table
      // This makes them available for all assistants in the org
      const response = await fetch("/api/composio/tools", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "sync",
          organizationId: organization.id,
          selectedTools, // Only sync selected tools
        }),
      });

      if (response.ok) {
        toast({
          title: "Tools Configured",
          description: `${selectedTools.length} tools are now available for your assistants`,
        });
      }
    } catch (error) {
      console.error("Error saving tools:", error);
      toast({
        title: "Error",
        description: "Failed to save tool selection. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSavingTools(false);
    }
  };

  const handleConnect = async (integration: any) => {
    // Check if app is configured
    if (!configuredApps.includes(integration.id)) {
      toast({
        title: "Integration Not Available",
        description:
          "Please contact your administrator to enable this integration.",
        variant: "destructive",
      });
      return;
    }

    // Get the auth config ID for this integration
    const authConfigId = authConfigs[integration.id];
    if (!authConfigId) {
      toast({
        title: "Configuration Error",
        description:
          "Integration configuration is missing. Please contact your administrator.",
        variant: "destructive",
      });
      return;
    }

    try {
      setConnecting(integration.id);
      console.log(
        `🔗 Connecting ${integration.name} with auth config: ${authConfigId}`,
      );

      const response = await fetch("/api/composio/connect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          authConfigId,
          organizationId: organization?.id,
        }),
      });

      if (response.ok) {
        const { authUrl } = await response.json();
        // Redirect to OAuth flow
        window.location.href = authUrl;
      } else {
        // Handle different error status codes
        let errorTitle = "Connection Error";
        let errorDescription =
          "Failed to start connection process. Please try again.";

        try {
          const errorData = await response.json();

          if (response.status === 404) {
            errorTitle = "Not Configured";
            errorDescription =
              errorData.error ||
              "This integration hasn't been configured yet. Please contact your administrator.";
          } else if (response.status === 400) {
            errorTitle = "Invalid Request";
            errorDescription = errorData.error || "Invalid request parameters.";
          } else if (response.status === 403) {
            errorTitle = "Unauthorized";
            errorDescription =
              errorData.error ||
              "You don't have permission to access this integration.";
          } else if (response.status === 500) {
            errorTitle = "Server Error";
            errorDescription =
              errorData.error || "Internal server error occurred.";
          } else {
            errorDescription = errorData.error || errorDescription;
          }
        } catch (parseError) {
          // If we can't parse the error response, use generic message
          console.error("Failed to parse error response:", parseError);
        }

        toast({
          title: errorTitle,
          description: errorDescription,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error starting OAuth:", error);
      toast({
        title: "Connection Error",
        description: "Failed to start connection process. Please try again.",
        variant: "destructive",
      });
    } finally {
      setConnecting("");
    }
  };

  const handleDisconnect = async (integration: any) => {
    const connection = connections.find((c: any) => c.tool === integration.id);
    if (!connection) return;

    try {
      await integrationConfigService.deleteConnection(connection.id);
      await loadConnections();
      setIsDrawerOpen(false);
      toast({
        title: "Disconnected",
        description: `${integration.name} has been disconnected successfully.`,
      });
    } catch (error) {
      console.error("Error disconnecting:", error);
      toast({
        title: "Error",
        description: "Failed to disconnect integration. Please try again.",
        variant: "destructive",
      });
    }
  };

  const connectedCount = availableIntegrations.filter((i) =>
    isConnected(i.id),
  ).length;

  return (
    <>
      <SettingsContentWrapper
        title="Integrations"
        description="Connect your organization with external tools and services"
      >
        <div className="space-y-6">
          {/* Status and Search - Mobile Responsive */}
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            {/* Status */}
            {connectedCount > 0 && (
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="px-3 py-2">
                  <Check className="h-4 w-4 mr-2 text-green-600" />
                  {connectedCount} Connected
                </Badge>
              </div>
            )}

            {/* Search */}
            <div className="relative w-full md:max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search integrations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-10"
              />
            </div>
          </div>

          {/* Responsive Grid */}
          {filteredIntegrations.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {filteredIntegrations.map((integration) => {
                const connected = isConnected(integration.id);

                return (
                  <div key={integration.id}>
                    <Card className="hover:shadow-md transition-shadow h-full min-h-[160px] md:min-h-[180px]">
                      <CardContent className="p-3 md:p-4 h-full flex flex-col justify-between text-center">
                        {/* Icon */}
                        <div className="flex justify-center mb-2">
                          <div className="text-xl md:text-2xl flex-shrink-0">
                            {integration.icon}
                          </div>
                        </div>

                        {/* Content Container */}
                        <div className="flex-1 flex flex-col justify-between min-h-0">
                          {/* Name */}
                          <h3 className="font-medium text-xs md:text-sm mb-2 leading-tight overflow-hidden">
                            {integration.name}
                          </h3>

                          {/* Description - Only show on larger screens */}
                          <p className="text-xs text-gray-500 mb-3 leading-relaxed hidden lg:block overflow-hidden">
                            {integration.description}
                          </p>

                          {/* Connect Button */}
                          <div className="mt-auto">
                            {connected ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="w-full text-xs h-7 md:h-8 px-2"
                                onClick={() =>
                                  handleSelectIntegration(integration)
                                }
                              >
                                Manage
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                className="w-full text-xs h-7 md:h-8 px-2"
                                onClick={() =>
                                  handleSelectIntegration(integration)
                                }
                              >
                                Connect
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 px-4">
              <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-6" />
              <h3 className="text-xl font-medium text-gray-900 mb-3">
                No integrations available
              </h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Contact your administrator to configure integrations for your
                organization.
              </p>
            </div>
          )}
        </div>
      </SettingsContentWrapper>

      {/* Right Drawer - Mobile Responsive */}
      <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <SheetContent className="w-full sm:w-[60%] md:w-[70%] max-w-[1200px] overflow-y-auto">
          {selectedIntegration && (
            <>
              <SheetHeader className="pb-4 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">{selectedIntegration.icon}</div>
                    <div>
                      <SheetTitle className="text-lg">
                        {selectedIntegration.name}
                      </SheetTitle>
                      <SheetDescription className="text-sm">
                        {selectedIntegration.description}
                      </SheetDescription>
                    </div>
                  </div>
                  {isConnected(selectedIntegration.id) && (
                    <Badge className="bg-green-100 text-green-700">
                      <Check className="h-3 w-3 mr-1" />
                      Connected
                    </Badge>
                  )}
                </div>
              </SheetHeader>

              <div className="py-4 space-y-4">
                {isConnected(selectedIntegration.id) ? (
                  <div className="space-y-4">
                    {connections.find(
                      (c: any) => c.tool === selectedIntegration.id,
                    )?.connection_data?.email && (
                      <div className="p-3 bg-gray-50 rounded-md">
                        <p className="text-sm text-gray-600">
                          Connected as:{" "}
                          <span className="font-medium text-gray-900">
                            {
                              connections.find(
                                (c: any) => c.tool === selectedIntegration.id,
                              )?.connection_data?.email
                            }
                          </span>
                        </p>
                      </div>
                    )}

                    {/* Tools Configuration Section */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-gray-900">
                          Available Tools
                        </h4>
                        {availableTools.length > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {selectedTools.length}/{availableTools.length}{" "}
                            selected
                          </Badge>
                        )}
                      </div>

                      {loadingTools ? (
                        <div className="flex justify-center py-6">
                          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                        </div>
                      ) : availableTools.length > 0 ? (
                        <>
                          <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
                            {availableTools.map((tool: any) => (
                              <label
                                key={tool.name}
                                className="flex items-center p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                              >
                                <Checkbox
                                  id={tool.name}
                                  checked={selectedTools.includes(tool.name)}
                                  onCheckedChange={() =>
                                    handleToolToggle(tool.name)
                                  }
                                  className="mr-3"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {tool.displayName ||
                                      tool.name?.replace(/_/g, " ")}
                                  </p>
                                  {tool.description && (
                                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                                      {tool.description}
                                    </p>
                                  )}
                                </div>
                              </label>
                            ))}
                          </div>

                          <div className="flex justify-between items-center">
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-blue-600 hover:text-blue-700"
                                onClick={() =>
                                  handleConnect(selectedIntegration)
                                }
                                disabled={connecting === selectedIntegration.id}
                              >
                                {connecting === selectedIntegration.id ? (
                                  <>
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    Reconnecting...
                                  </>
                                ) : (
                                  <>Reconnect</>
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() =>
                                  handleDisconnect(selectedIntegration)
                                }
                              >
                                Disconnect
                              </Button>
                            </div>
                            <Button
                              size="sm"
                              onClick={handleSaveTools}
                              disabled={
                                savingTools || selectedTools.length === 0
                              }
                            >
                              {savingTools ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                  Saving...
                                </>
                              ) : (
                                <>Save {selectedTools.length} Selected Tools</>
                              )}
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div className="flex justify-between items-center">
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-blue-600 hover:text-blue-700"
                              onClick={() => handleConnect(selectedIntegration)}
                              disabled={connecting === selectedIntegration.id}
                            >
                              {connecting === selectedIntegration.id ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                  Reconnecting...
                                </>
                              ) : (
                                <>Reconnect</>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() =>
                                handleDisconnect(selectedIntegration)
                              }
                            >
                              Disconnect
                            </Button>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              loadToolsForIntegration(selectedIntegration)
                            }
                          >
                            Load Available Tools
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h5 className="font-medium text-gray-900 mb-2">
                        Ready to connect?
                      </h5>
                      <p className="text-sm text-gray-600">
                        Click the button below to authenticate and start using
                        this integration.
                      </p>
                    </div>

                    <div className="flex justify-center">
                      <Button
                        className="bg-[#8aa3d9] hover:bg-[#7a93c9] text-white"
                        disabled={connecting === selectedIntegration.id}
                        onClick={() => handleConnect(selectedIntegration)}
                      >
                        {connecting === selectedIntegration.id ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            Connecting...
                          </>
                        ) : (
                          `Connect ${selectedIntegration.name}`
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};

export default IntegrationsPageContent;
